import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import without from 'lodash/without';
import DropdownMenu from '@radix-ui/react-dropdown-menu';
import { SuiDropdownSelect } from '@splashthat/sui';
import { useFlags } from 'utils/react-context';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';

import ChevronDown from 'common/icons/ChevronDown';
import { useNode } from '@splashthat/splash-cms-engine';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import {
    SuiDropdownSelectWrapper,
    SessionFilterDropdownCustomStyles,
    StyledDropdownMenuTrigger,
    StyledDropdownMenuContent,
    Filterlabel,
    FilterIndicator,
} from './SessionFilter.styles';
import SessionTagsSubFilter from './SessionTagsSubFilter';
import SessionDatesSubFilter from './SessionDatesSubFilter';
import SessionStartTimesSubFilter from './SessionStartTimesSubFilter';
import SessionLocationsSubFilter from './SessionLocationsSubFilter';

export default function SessionFilter({
    meta: { placeholder, labelTags, labelDates, labelStartTimes, labelLocations },
    styles: {
        backgroundColor,
        backgroundColorHover,
        fontColor,
        fontColorHover,
        iconColor,
        fontSize,
        fontFamily,
        borderColor,
        borderColorHover,
        borderWidth,
        borderRadius,
    },
    selected,
    hovered,
}) {
    const node = useNode();
    const {
        theme: { activeFonts, colors },
    } = useTouchpointData();
    const sessions = useSessions();
    const flags = useFlags();
    const selectedTags = sessions.uniqueTags.filter((tag) => sessions.filters.tags.includes(tag));
    const dropdownDisplayValue = selectedTags.join(', ');
    const customCssClassName = getCustomCssClassName(node);

    const dropdownSelectedOptions = sessions.uniqueTags.reduce(
        (acc, tag) => ({
            ...acc,
            [tag]: sessions.filters.tags.includes(tag),
        }),
        {},
    );

    const onFilteredTagSelect = (tagName) => {
        const updatedSessionTagFilterIds = sessions.filters.tags.includes(tagName)
            ? without(sessions.filters.tags, tagName) // unselect the checkbox
            : sessions.filters.tags.concat(tagName); //  select the checkbox

        sessions.filters.setTags(updatedSessionTagFilterIds);
    };

    // Convert the { id, name } structure to { id, label } for SUI parsing
    const suiFriendlySessionsTagsList = sessions.uniqueTags.map((name) => ({
        id: name,
        label: name,
    }));

    const anySessionHasTags = useMemo(() => sessions.data.some((session) => session.tags?.length > 0), [sessions.data]);

    const appliedFilterCount =
        sessions.filters.dates?.length +
        sessions.filters.startTimes?.length +
        sessions.filters.tags?.length +
        sessions.filters.locations?.length;

    return (
        <Fragment>
            <SessionFilterDropdownCustomStyles
                $borderColor={colors[borderColor]}
                $fontColor={colors[fontColor]}
                $fontFamily={activeFonts[fontFamily]}
                $backgroundColor={colors[backgroundColor]}
                $backgroundColorHover={colors[backgroundColorHover]}
                $iconColor={colors[iconColor]}
            />
            <SuiDropdownSelectWrapper
                selected={selected}
                hovered={hovered}
                $fontFamily={activeFonts[fontFamily]}
                $fontColor={colors[fontColor]}
                $fontColorHover={colors[fontColorHover]}
                $iconColor={colors[iconColor]}
                $backgroundColor={colors[backgroundColor]}
                $backgroundColorHover={colors[backgroundColorHover]}
                $borderColor={colors[borderColor]}
                $borderColorHover={colors[borderColorHover]}
                $borderWidth={borderWidth}
                $borderRadius={borderRadius}
                $fontSize={fontSize}
                onClick={node.actions.onClick()}
                onMouseMove={node.actions.onMouseMove()}
                onMouseLeave={node.actions.onMouseLeave()}
                className={customCssClassName}
            >
                {flags.isAgendaSessionRegistrationEnabledPhase2 ? (
                    <DropdownMenu.Root modal={false}>
                        <StyledDropdownMenuTrigger
                            $fontColor={colors[fontColor]}
                            $backgroundColor={colors[backgroundColor]}
                            $borderRadius={borderRadius}
                            $borderColorHover={colors[borderColorHover]}
                            $borderWidth={borderWidth.top}
                            $borderColor={colors[borderColor]}
                            $fontSize={fontSize}
                            $fontFamily={activeFonts[fontFamily]}
                            $backgroundColorHover={colors[backgroundColorHover]}
                            $iconColor={colors[iconColor]}
                        >
                            <Filterlabel>
                                {placeholder}
                                {appliedFilterCount > 0 ? (
                                    <FilterIndicator
                                        $backgroundColor={colors[fontColor]}
                                        $fontColor={colors[backgroundColor]}
                                    >
                                        {appliedFilterCount}
                                    </FilterIndicator>
                                ) : null}
                            </Filterlabel>
                            <ChevronDown />
                        </StyledDropdownMenuTrigger>
                        <StyledDropdownMenuContent align={'start'}>
                            <DropdownMenu.Separator />
                            {anySessionHasTags && (
                                <SessionTagsSubFilter
                                    displayLabel={labelTags}
                                    fontColor={fontColor}
                                    backgroundColor={backgroundColor}
                                    fontSize={fontSize}
                                    fontFamily={fontFamily}
                                    backgroundColorHover={backgroundColorHover}
                                    iconColor={colors[iconColor]}
                                    borderColor={colors[borderColor]}
                                />
                            )}
                            <SessionDatesSubFilter
                                displayLabel={labelDates}
                                fontColor={fontColor}
                                backgroundColor={backgroundColor}
                                fontSize={fontSize}
                                fontFamily={fontFamily}
                                backgroundColorHover={backgroundColorHover}
                                iconColor={colors[iconColor]}
                                borderColor={colors[borderColor]}
                            />
                            <SessionStartTimesSubFilter
                                displayLabel={labelStartTimes}
                                fontColor={fontColor}
                                backgroundColor={backgroundColor}
                                fontSize={fontSize}
                                fontFamily={fontFamily}
                                backgroundColorHover={backgroundColorHover}
                                iconColor={colors[iconColor]}
                                borderColor={colors[borderColor]}
                            />
                            {!!sessions?.uniqueLocations?.length && (
                                <SessionLocationsSubFilter
                                    displayLabel={labelLocations}
                                    fontColor={fontColor}
                                    backgroundColor={backgroundColor}
                                    fontSize={fontSize}
                                    fontFamily={fontFamily}
                                    backgroundColorHover={backgroundColorHover}
                                    iconColor={colors[iconColor]}
                                    borderColor={colors[borderColor]}
                                />
                            )}
                        </StyledDropdownMenuContent>
                    </DropdownMenu.Root>
                ) : (
                    <SuiDropdownSelect
                        className="SessionFilterDropdown"
                        placeholder={placeholder}
                        value={dropdownDisplayValue}
                        options={suiFriendlySessionsTagsList}
                        onChange={onFilteredTagSelect}
                        selectedOptions={dropdownSelectedOptions}
                        data-qa="SessionFilter__dropdown-select"
                    />
                )}
            </SuiDropdownSelectWrapper>
        </Fragment>
    );
}

SessionFilter.splash = {
    related: {
        settings: React.lazy(() => import('./SessionFilter.tools')),
    },
    defaultProps: {
        meta: {
            placeholder: 'Filter',
            labelTags: 'Tags',
            labelDates: 'Dates',
            labelStartTimes: 'Start Times',
            labelLocations: 'Locations',
        },
        styles: {
            backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            backgroundColorHover: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            iconColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontColorHover: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontSize: 16,
            fontFamily: BRAND_LIBRARY_BODY_TEXT,
            borderColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            borderColorHover: BRAND_LIBRARY_DARK_TEXT_COLOR,
            borderWidth: {
                top: 1,
                bottom: 1,
                left: 1,
                right: 1,
            },
            borderRadius: 4,
        },
    },
};

SessionFilter.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    meta: PropTypes.shape({
        placeholder: PropTypes.string,
        labelTags: PropTypes.string,
        labelDates: PropTypes.string,
        labelStartTimes: PropTypes.string,
        labelLocations: PropTypes.string,
    }).isRequired,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        backgroundColorHover: PropTypes.number,
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
        borderColor: PropTypes.number,
        borderColorHover: PropTypes.number,
        borderStyle: PropTypes.string,
        borderWidth: PropTypes.object,
        borderRadius: PropTypes.number,
        iconColor: PropTypes.number,
    }).isRequired,
};
